import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { getStaticTours } from '../../data/staticTours';
import './style.css';

import BgImage from '../../assets/UI_Screens/Bg.webp';
import LogoImage from '../../assets/UI_Screens/iraq logo.webp';

function TourVideo({ url }) {
    const [cover, setCover] = useState(true);
    const [ended, setEnded] = useState(false);
    const iframeRef = useRef(null);

    const isYouTube = url?.includes('youtube.com') || url?.includes('youtu.be');

    // Build the embed URL once
    let embedUrl = url || '';
    let videoId = '';
    if (isYouTube) {
        const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
        if (match && match[1]) {
            videoId = match[1];
            embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&controls=0&modestbranding=1&rel=0&disablekb=1&iv_load_policy=3&enablejsapi=1&origin=${window.location.origin}`;
        }
    } else if (url?.includes('drive.google.com')) {
        const fileIdMatch = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)\//) || url.match(/\?id=([a-zA-Z0-9_-]+)/);
        if (fileIdMatch && fileIdMatch[1]) {
            embedUrl = `https://drive.google.com/file/d/${fileIdMatch[1]}/preview`;
        }
    }

    // Fallback: Brief cover to hide YouTube title/thumbnail flash — lifts when playing or after 2s
    useEffect(() => {
        const timer = setTimeout(() => setCover(false), 2000);
        return () => clearTimeout(timer);
    }, []);

    // Connect official YouTube API to the fast iframe to detect when it ends
    useEffect(() => {
        if (!isYouTube) return;

        const initPlayer = () => {
            if (!iframeRef.current) return;
            // The YT API will attach to the existing iframe because enablejsapi=1 is in the src
            new window.YT.Player(iframeRef.current, {
                events: {
                    onStateChange: (event) => {
                        if (event.data === window.YT.PlayerState.PLAYING) {
                            setCover(false);
                        } else if (event.data === window.YT.PlayerState.ENDED) {
                            setEnded(true);
                        }
                    }
                }
            });
        };

        if (window.YT && window.YT.Player) {
            initPlayer();
        } else {
            const tag = document.createElement('script');
            tag.src = "https://www.youtube.com/iframe_api";
            if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
                const firstScriptTag = document.getElementsByTagName('script')[0];
                firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
                window.onYouTubeIframeAPIReady = initPlayer;
            } else {
                const checkYT = setInterval(() => {
                    if (window.YT && window.YT.Player) {
                        clearInterval(checkYT);
                        initPlayer();
                    }
                }, 100);
            }
        }
    }, [isYouTube]);

    const handleReplay = () => {
        setEnded(false);
        setCover(true);
        // Reload the iframe by resetting src
        if (iframeRef.current) {
            iframeRef.current.src = embedUrl;
        }
        setTimeout(() => setCover(false), 500);
    };

    return (
        <div style={{ position: 'relative', width: '100%', height: '100%', backgroundColor: 'black' }}>
            <iframe
                ref={iframeRef}
                src={embedUrl}
                title="Tour Introduction Video"
                frameBorder="0"
                allow="autoplay; encrypted-media; fullscreen"
                allowFullScreen
                id="tour-video-iframe"
                className="intro-video-iframe"
            ></iframe>

            {cover && (
                <div style={{
                    position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                    backgroundColor: 'black', zIndex: 10, display: 'flex', justifyContent: 'center', alignItems: 'center'
                }}>
                </div>
            )}

            {ended && !cover && (
                <div style={{
                    position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                    backgroundColor: '#000', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10
                }}>
                    <button 
                        onClick={handleReplay} 
                        style={{ 
                            background: 'transparent', 
                            border: 'none', 
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '20px',
                            transition: 'transform 0.2s',
                            outline: 'none'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                        onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    >
                        <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="1 4 1 10 7 10"></polyline>
                            <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path>
                        </svg>
                    </button>
                </div>
            )}
        </div>
    );
}

function Intro() {
    const [step, setStep] = useState(1);
    const [fading, setFading] = useState({ state: 'in', nextStep: null });
    const [progress, setProgress] = useState(0);
    const [tours, setTours] = useState([]);
    const [activeTour, setActiveTour] = useState(null);
    const containerRef = useRef(null);
    const history = useHistory();

    // Fetch tours on mount
    useEffect(() => {
        try {
            const data = getStaticTours();
            if (Array.isArray(data)) {
                setTours(data);
            }
        } catch (err) {
            console.error("Could not load static tours", err);
        }
    }, []);

    const handleBegin = () => {
        // Fade out screen 1, move to screen 2
        setFading({ state: 'out', nextStep: 2 });
        setTimeout(() => {
            setStep(2);
            setFading({ state: 'in', nextStep: null });
        }, 400); // Wait for fadeOut animation
    };

    useEffect(() => {
        if (step === 2 && fading.state === 'in') {
            // Start progress bar animation
            let perc = 0;
            const interval = setInterval(() => {
                perc += 2;
                setProgress(perc);
                if (perc >= 100) {
                    clearInterval(interval);
                    // Transition to screen 3
                    setTimeout(() => {
                        setFading({ state: 'out', nextStep: 3 });
                        setTimeout(() => {
                            setStep(3);
                            setFading({ state: 'in', nextStep: null });
                        }, 400);
                    }, 200);
                }
            }, 40); // 40ms * 50 = 2000ms
            return () => clearInterval(interval);
        }
    }, [step, fading]);

    const handleTourClick = (tour) => {
        if (tour.introVideo) {
            setFading({ state: 'out', nextStep: 4 });
            setTimeout(() => {
                setActiveTour(tour);
                setStep(4);
                setFading({ state: 'in', nextStep: null });
            }, 400);
        } else {
            if (containerRef.current) {
                containerRef.current.scrollTo({ top: 0, behavior: 'auto' });
            }
            window.scrollTo(0, 0);
            setFading({ state: 'to-black', nextStep: null });
            setTimeout(() => {
                history.push(`/index?tour=${tour.id}`);
            }, 400);
        }
    };

    const handleVideoBack = () => {
        setFading({ state: 'out', nextStep: 3 });
        setTimeout(() => {
            setActiveTour(null);
            setStep(3);
            setFading({ state: 'in', nextStep: null });
        }, 400);
    };

    const handleVideoContinue = () => {
        if (!activeTour) return;
        if (containerRef.current) {
            containerRef.current.scrollTo({ top: 0, behavior: 'auto' });
        }
        window.scrollTo(0, 0);
        setFading({ state: 'to-black', nextStep: null });
        setTimeout(() => {
            history.push(`/index?tour=${activeTour.id}`);
        }, 400);
    };



    const currentClass = fading.state === 'in' ? 'fade-in' : (fading.state === 'to-black' ? '' : 'fade-out');

    return (
        <div ref={containerRef} className="intro-container" style={{ backgroundImage: `url(${BgImage})` }}>
            {fading.state === 'to-black' && <div className="black-overlay-transition"></div>}
            <div className="intro-overlay"></div>
            <img src={LogoImage} alt="Logo" className="intro-logo" />

            <div className={`intro-content ${currentClass}`}>
                {step === 1 && (
                    <div className="screen1">
                        <h1>Explore<br />Karbala in 360°</h1>
                        <p>
                            Explore Karbala's holy sites through immersive 360° web views.<br />
                            Begin a journey of reflection and peace.
                        </p>
                        <button className="begin-btn" onClick={handleBegin}>Begin Ziarat</button>
                    </div>
                )}

                {step === 2 && (
                    <div className="screen2">
                        <h2>Entering the<br />Sacred Journey</h2>
                        <p>Preparing Your Journey...</p>
                        <div className="intro-progress-container">
                            <div className="intro-progress-bar" style={{ width: `${progress}%` }}></div>
                        </div>
                    </div>
                )}

                {step === 3 && (
                    <div className="screen3">
                        <div className="screen3-header">
                            <h2>Choose Your<br />Sacred Journey</h2>
                            <p>
                                Choose a sacred place to begin your 360° journey of reflection. Experience the holy sites of Karbala like never before, from every angle.
                            </p>
                        </div>

                        <div className="tours-container">
                            {tours.map(tour => {
                                // Determine best image for card. 
                                // We don't have direct access to a thumbnail field, so let's fallback to the background image if none available, 
                                // but tours might have scenes we can guess an image from. If not, we just use a dark gradient to show text clearly.
                                // We'll just define the style with a solid dark grey if we have no image, or let the CSS manage it.
                                return (
                                    <div
                                        key={tour.id}
                                        className="tour-card"
                                        onClick={() => handleTourClick(tour)}
                                    >
                                        <div className="tour-card-bg" style={{
                                            backgroundColor: 'rgba(0,0,0,0.6)',
                                            backgroundImage: tour.thumbnail ?
                                                `url("${window.location.origin}/${((tour.baseFolder || '').replace(/^public\/?/, '') + '/' + tour.thumbnail).replace(/^\//, '')}")` :
                                                `url("${BgImage}")`
                                        }}></div>
                                        <div className="tour-card-overlay">
                                            <h3 className="tour-card-title">{tour.name}</h3>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {step === 4 && activeTour && (
                    <div className="screen4">
                        <div className="screen4-header">
                            <h2>Introduction to <br />{activeTour.name}</h2>
                        </div>
                        <div className="video-wrapper">
                            <TourVideo url={activeTour.introVideo} />
                        </div>
                        <div className="video-actions">
                            <button className="video-btn back-btn" onClick={handleVideoBack}>Back</button>
                            <button className="video-btn continue-btn" onClick={handleVideoContinue}>Continue to Tour</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Intro;
